import { useMemo } from "react";

import { useConfig } from "./useConfig";
import { useCurrentUser } from "./useCurrentUser";

/**
 * `true` / `false` once both inputs have resolved; `undefined` while either is
 * still in flight. Callers that render a control on `true` therefore hide it
 * during load, which is the correct direction for a permission.
 *
 * 🔴 A `false` HERE DOES NOT MEAN "THE USER LACKS THE PERMISSION". It means one
 * of three unrelated things, and until 2026-09-04 all three logged the same
 * `console.debug` line — at `debug` level, which Chrome DevTools hides unless
 * Verbose is ticked. That is why the Flights kebab's "Types and Tabs" entry
 * (FlightsList.tsx, gated on `user_management`/`edit`) was reported as
 * "clobbered" and hunted repeatedly through the bundle, the menu structure and
 * the config cache: the control was present and correct the whole time, the
 * gate was answering `false`, and nothing on screen or in the default console
 * said which of the three inputs was missing.
 *
 * The three cases, now distinguished below:
 *
 *   1. `config[permission]` absent — the tenant has no `config` row of
 *      `config_type='permission'` for this module/variable, so there is no
 *      permission id to look for. STRUCTURAL: nobody can ever pass.
 *   2. `currentUser.permissions[module]` absent — the module is not in the
 *      user's bag AT ALL. STRUCTURAL, and not the same as an empty grant.
 *      `/v6/users/me` builds that bag in `get_user_permission_list()`
 *      (aeronet-fast-api/app/users/permissions.py), which restricts it to
 *      `get_user_modules()` — and that applies `_product_filter()`, so a
 *      `modules` row whose `products` column does not carry the request's
 *      product token is dropped along with every grant hanging off it. On
 *      `module_g2_demo` 2026-09-04 all three `user_management` rows lost:
 *      ids 2 and 10210 (which carry `permissions='1296;1297'`) are tagged
 *      `products='retired'` and are filtered out, and id 210 (`products=''`)
 *      survives the filter but carries `permissions=''`. The key is therefore
 *      absent for every user under every product token, so this gate could not
 *      return `true` on that tenant no matter who logged in.
 *   3. Both present, id not in the array — a GENUINE denial. This is the only
 *      case that means what the function name suggests.
 *
 * Cases 1 and 2 are `console.warn`, because they are misconfiguration and
 * somebody needs to see them; case 3 stays `console.debug`, because it is
 * ordinary and would otherwise be noise on every render for every ungranted
 * control.
 *
 * ⚠️ DO NOT "FIX" A STRUCTURAL FALSE BY DEFAULTING THIS TO `true`. Hiding a
 * control you should be able to see is annoying; showing one you should not is
 * a permission bypass, and this codebase has spent real time removing exactly
 * that. Cases 1 and 2 are DATA defects on the tenant and are fixed in the
 * tenant's `config` / `modules` / `modules_groups` rows, not here.
 */
/**
 * The permission id for `permission` in a module's reduced config, matched the
 * way the SERVER matches it: case-insensitively.
 *
 * `/v6/config` strips the `<function>_` prefix and keeps the rest of
 * `config_variable` VERBATIM (aeronet-fast-api app/config/schemas.py,
 * `removeprefix`), so the key's case is whatever the tenant row holds. The
 * rows do not agree: `flightduty_Edit` arrives as `Edit`, but `flights_edit`
 * (848) and `load_sheet_edit` (3947) arrive as `edit` — measured on g2, crm,
 * aquipe, mustang and peak, 2026-09-28. The server's own lookup for the same
 * gate (`get_config_value`, app/config/crud.py) compares `func.lower()` on both
 * sides, so `HasPermission("flights","Edit")` passes there while
 * `useHasPermission("flights","Edit")` read `config["Edit"]`, got `undefined`,
 * and hid Add flight (H52) and the Fuel & W/B tab (H43) from EVERY user.
 *
 * Exact key first, so a tenant that ever holds two keys differing only by case
 * (none does today — checked across every `config_type='permission'` row on all
 * five) keeps the one the caller named. This is a lookup fix, not a default:
 * no matching key is still `undefined`, and the gate still returns `false`.
 */
export function resolvePermissionId(
  config: { [key: string]: any },
  permission: string
): any {
  if (config[permission] !== undefined) return config[permission];
  const wanted = permission.toLowerCase();
  const key = Object.keys(config).find((k) => k.toLowerCase() === wanted);
  return key === undefined ? undefined : config[key];
}

export function useHasPermission(
  module: string,
  permission: string
): boolean | undefined {
  return useHasPermissionUnder(module, permission, [module]);
}

/**
 * `<configModule>_<permission>` granted under ANY of `grantKeys`: the id comes from
 * `configModule`'s config rows, the grant may sit on a module row of any of these
 * `mod_function`s. `useHasPermission(m, p)` is this with `[m]`.
 *
 * The case it exists for: a permission whose config rows say one module and whose
 * grants sit on another. Every `jobs_*` id (jobs_task_edit 742, …) is configured
 * under `jobs`, but on mustang, g2, peak, demo/scm and crm it is granted on the
 * Work Orders rows, whose mod_function is `workorders` (aquipe: `jobs`). So
 * `useHasPermission("jobs", "task_edit")` read `permissions.jobs`, missed the
 * grant and said "You don't have permission" to every user, admins included, on
 * the WO Tasks tab (Aaron 2026-10-12, mustang W/O 337). The server resolved the
 * same pair long before (aeronet-fast-api app/jobs/access.py, WORKORDER_GRANT_KEYS;
 * the C17 class). Use the module's own wrapper (jobs/workOrderPermission.ts
 * `useWorkOrderPermission`), not the raw keys, so client and server name one pair.
 */
export function useHasPermissionUnder(
  configModule: string,
  permission: string,
  grantKeys: readonly string[]
): boolean | undefined {
  const currentUser = useCurrentUser();
  const config = useConfig(configModule);
  const keysId = grantKeys.join("\u0000");

  const hasPermission: boolean | undefined = useMemo(() => {
    if (config && currentUser) {
      const permissionId = resolvePermissionId(config, permission);
      // Optional-chained deliberately: `permissions` is a required field on the
      // `/users/me` schema, but a bare `currentUser.permissions[module]` throws
      // a TypeError inside this useMemo if it is ever absent, which white-
      // screens the whole page rather than hiding one control. Absent bag =>
      // `false`, which is the safe direction.
      const bags = grantKeys
        .map((key) => currentUser.permissions?.[key])
        .filter((bag): bag is number[] => Array.isArray(bag));
      const where = grantKeys.join("' / '");

      if (!permissionId) {
        console.warn(
          `useHasPermission: no '${permission}' permission id configured for module '${configModule}' — ` +
            `expected a config row with config_function='${configModule}', config_variable='${configModule}_${permission}' ` +
            `(matched case-insensitively), ` +
            `config_type='permission'. Gate returns false for every user on this tenant.`
        );
        return false;
      }

      if (bags.length === 0) {
        console.warn(
          `useHasPermission: module '${where}' is absent from this user's permission bag, so ` +
            `'${permission}' cannot be granted. The bag comes from /v6/users/me and is filtered by ` +
            `modules.products for the current product — a grant sitting on a module row that is not ` +
            `tagged for this product is dropped. Gate returns false. Bag keys: ` +
            `${Object.keys(currentUser.permissions ?? {}).join(", ") || "(none)"}`
        );
        return false;
      }

      if (bags.some((bag) => bag.includes(Number(permissionId)))) {
        console.debug(`has ${configModule} ${permission} permission = true`);
        return true;
      }

      console.debug(
        `has ${configModule} ${permission} permission = false (denied: id ${permissionId} not under '${where}')`
      );
      return false;
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- grantKeys is keyed by keysId
  }, [config, currentUser, configModule, permission, keysId]);

  return hasPermission;
}

export default useHasPermission;
